import { useNavigate } from "react-router-dom";
import { Check, X, Crown, Sparkles } from "lucide-react";
import { PublicNav } from "@/components/PublicNav";
import { Footer } from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";

const FEATURES = [
  ["Create profile & browse", true, true, true],
  ["Basic search & matches", true, true, true],
  ["Send & receive Interest", true, true, true],
  ["Shortlist & basic chat", true, true, true],
  ["Basic AI recommendations", true, true, true],
  ["Privacy controls, block & report", true, true, true],
  ["Advanced search & filters", false, true, true],
  ["Advanced AI matching", false, true, true],
  ["Compatibility Lab", false, true, true],
  ["Voice & video calling", false, true, true],
  ["Contact requests", false, true, true],
  ["Who viewed you", false, true, true],
  ["Premium visibility & boost", false, true, true],
];

export function PricingTable({ embedded }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const go = () => nav(user ? "/app/subscription" : "/register");
  return (
    <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto" data-testid="pricing-table">
      {/* Free */}
      <div className="mj-card p-8 flex flex-col">
        <h3 className="font-serif text-2xl font-semibold text-[#261A1C]">Free</h3>
        <div className="mt-3"><span className="font-serif text-4xl font-bold text-[#58111A]">₹0</span><span className="text-sm text-[#605255]"> forever</span></div>
        <p className="text-sm text-[#605255] mt-2">Everything to get started.</p>
        <button onClick={go} className="btn-outline mt-6" data-testid="choose-free">Get Started</button>
        <FeatureList plan={1} />
      </div>
      {/* Premium */}
      <div className="p-8 flex flex-col rounded-2xl relative shadow-xl md:-translate-y-2 bg-gradient-to-b from-[#FAF5EE] to-white border-2 border-[#C59B27]">
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 chip">Most Flexible</span>
        <h3 className="font-serif text-2xl font-semibold text-[#261A1C] flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#C59B27]" /> Premium</h3>
        <div className="mt-3"><span className="font-serif text-4xl font-bold text-[#58111A]">₹150</span><span className="text-sm text-[#605255]"> / month</span></div>
        <p className="text-sm text-[#605255] mt-2">Everything in Free, plus premium tools.</p>
        <button onClick={go} className="btn-primary mt-6" data-testid="choose-premium">Upgrade for ₹150/month</button>
        <FeatureList plan={2} />
      </div>
      {/* Lifetime */}
      <div className="p-8 flex flex-col rounded-2xl bg-[#3B0A12] text-white border border-[#C59B27]/40 shadow-2xl">
        <span className="chip w-fit">Best for Long-Term</span>
        <h3 className="font-serif text-2xl font-semibold mt-3 flex items-center gap-2"><Crown className="w-5 h-5 text-[#C59B27]" /> Lifetime</h3>
        <div className="mt-3"><span className="font-serif text-4xl font-bold text-[#E2C382]">₹2,000</span><span className="text-sm text-[#D8B4B9]"> one-time</span></div>
        <p className="text-sm text-[#D8B4B9] mt-2">All Premium features, permanently.</p>
        <button onClick={go} className="btn-gold mt-6" data-testid="choose-lifetime">Get Lifetime for ₹2,000</button>
        <FeatureList plan={3} dark />
      </div>
    </div>
  );
}

function FeatureList({ plan, dark }) {
  return (
    <ul className="mt-6 space-y-2.5">
      {FEATURES.map((row) => {
        const on = row[plan];
        return (
          <li key={row[0]} className={`flex items-start gap-2 text-sm ${dark ? "text-[#E7CDCF]" : "text-[#261A1C]"} ${on ? "" : "opacity-45"}`}>
            {on ? <Check className="w-4 h-4 text-[#15803D] mt-0.5 shrink-0" /> : <X className="w-4 h-4 mt-0.5 shrink-0" />} {row[0]}
          </li>
        );
      })}
    </ul>
  );
}

export default function Pricing() {
  return (
    <div className="bg-[#FDFBF7] min-h-screen" data-testid="pricing-page">
      <PublicNav />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <div className="text-xs uppercase tracking-wider text-[#C59B27] font-semibold">Simple, transparent pricing</div>
        <h1 className="font-serif text-4xl sm:text-5xl font-bold text-[#58111A] mt-2">Choose your plan</h1>
        <p className="text-[#605255] mt-3 max-w-xl mx-auto">Start free. Upgrade to Premium for ₹150/month or get Lifetime access for ₹2,000 — no hidden tiers.</p>
        <div className="mt-12"><PricingTable /></div>
      </div>
      <Footer />
    </div>
  );
}
